import { useEffect, useState, useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { getTranslation } from "../utils/translations";

export default function ProviderBookings({ providerId, branchId, token, refreshTrigger }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  const [notice, setNotice] = useState("");

  const { lang } = useContext(LanguageContext);
  const t = getTranslation(lang);

  const showNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 2500);
  };

  // Fetch bookings
  useEffect(() => {
    if (!providerId) return;

    async function fetchBookings() {
      try {
        setLoading(true);
        setError(null);

        let url = `${process.env.REACT_APP_API_URL}/api/reservations/provider/${providerId}`;
        if (branchId) url += `?branchId=${branchId}`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.message || "Failed to fetch reservations");
        }

        const data = await res.json();

        // Map PostgreSQL fields to client format
        const mapped = data.map(b => ({
          id: b.reservation_id,
          branchId: b.branch_id,
          branchName: b.branch_name,
          dateTime: b.date_time,
          guests: b.guests,
          phone: b.phone,
          userEmail: b.user_email,
          name: b.user_name || "Guest",
          status: b.status || "pending"
        }));

        mapped.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setBookings(mapped);
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, [providerId, branchId, token, refreshTrigger]);



  const handleConfirmBooking = async (reservationId) => {
    try {
      setConfirmingId(reservationId);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}/confirm`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to confirm reservation");
      }

      setBookings(prev =>
        prev.map(b =>
          b.id === reservationId ? { ...b, status: "confirmed" } : b
        )
      );
    } catch (err) {
      console.error(err);
      showNotice(err.message || "Failed to confirm reservation");
    } finally {
      setConfirmingId(null);
    }
  };


  // Cancel reservation
  const handleCancelBooking = async (reservationId) => {
    try {
      setCancelingId(reservationId);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to cancel reservation");
      }

      setBookings(prev => prev.filter(b => b.id !== reservationId));
    } catch (err) {
      console.error(err);
      showNotice(err.message || "Failed to cancel reservation");
    } finally {
      setCancelingId(null);
      setConfirmCancelId(null);
    }
  };

  if (loading) return <p style={{ color: "#fff" }}>{t.provider_bookings.loading}</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!bookings.length) return <p style={{ color: "#9ca3af" }}>{t.provider_bookings.no_reservations}</p>;

  return (
    <>
      {notice && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "max-content",
            maxWidth: "calc(100vw - 32px)",
            textAlign: "center",
            background: "#ffffff",
            color: "#111",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "14px",
            fontWeight: "500"
          }}
        >
          {notice}
        </div>
      )}

      {confirmCancelId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000
          }}
        >
          <div
            style={{
              background: "#ffffff",
              padding: "20px",
              borderRadius: "10px",
              width: "320px",
              maxWidth: "calc(100vw - 32px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              textAlign: "center"
            }}
          >
            <p style={{ color: "#111", marginBottom: "20px", fontWeight: "500" }}>
              {t.provider_bookings.confirm_cancel}
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={() => setConfirmCancelId(null)}
                style={{
                  padding: "8px 14px",
                  background: "#6b7280",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t.provider_bookings.cancel}
              </button>

              <button
                onClick={() => handleCancelBooking(confirmCancelId)}
                style={{
                  padding: "8px 14px",
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t.provider_bookings.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ maxHeight: "300px", overflowY: "auto" }}>
        {bookings.map((b) => (
          <div
            key={b.id}
            style={{
              padding: 8,
              marginBottom: 6,
              borderRadius: 8,
              background: "#fff",
              color: "#000",
              border: "1px solid #374151",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}
          >
            <p><strong>{t.provider_bookings.date_time}:</strong> {new Date(b.dateTime).toLocaleString()}</p>
            <p><strong>{t.provider_bookings.number_of_guests}:</strong> {b.guests}</p>
            <p><strong>{t.provider_bookings.reserver_name}:</strong> {b.name}</p>
            <p><strong>{t.provider_bookings.reserver_email}:</strong> {b.userEmail}</p>
            {b.phone && <p><strong>{t.provider_bookings.reserver_phone}:</strong> {b.phone}</p>}

            <button
              onClick={() => setConfirmCancelId(b.id)}
              disabled={cancelingId === b.id}
              style={{
                padding: "6px 12px",
                background: cancelingId === b.id ? "#f97316" : "#ef4444",
                color: "#fff",
                borderRadius: 6,
                cursor: cancelingId === b.id ? "not-allowed" : "pointer",
                fontWeight: "bold",
                alignSelf: "flex-start",
                marginTop: 4
              }}
            >
              {cancelingId === b.id ? t.provider_bookings.cancelling : t.provider_bookings.cancel_reservation}
            </button>

            {b.status !== "confirmed" ? (
              <button
                onClick={() => handleConfirmBooking(b.id)}
                disabled={confirmingId === b.id}
                style={{
                  padding: "6px 12px",
                  background: "#22c55e",
                  color: "#fff",
                  borderRadius: 6,
                  cursor: confirmingId === b.id ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                  alignSelf: "flex-start",
                  marginTop: 4
                }}
              >
                {confirmingId === b.id ? t.provider_bookings.confirming : t.provider_bookings.confirm_reservation}
              </button>
            ) : (
              <button
                disabled
                style={{
                  padding: "6px 12px",
                  background: "#16a34a",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: "bold",
                  alignSelf: "flex-start",
                  marginTop: 4
                }}
              >
                {t.provider_bookings.confirmed}
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
